import React, { useState, useEffect } from 'react';
import { titleize, truncate } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import AddButton from 'components/v2/buttons/AddButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import EditButton from 'components/v2/buttons/EditButton';
import {
  useGetDocumentLinks,
  useCreateDocumentLinks,
  useDeleteDocumentLinks,
  useHandleUpdateDocumentsLink,
} from 'utils/DocumentsHooks';
import { showToast } from 'stores/ToastStore';
import CargomaticCard from 'components/v2/card';
import CargomaticModal from 'components/v2/modal';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import LoadingCircle from 'components/ui/LoadingCircle';
import Table from 'components/v2/Table';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import SopForm from './SopForm';

const pageTestId = 'shipper-sop';

export default function SopDocuments({ userId }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [sop, setSop] = useState(null);
  const { t } = useTranslation();
  const { data, error, refetch } = useGetDocumentLinks({
    foreignKeys: [userId],
  });
  const [deleteDocuments] = useDeleteDocumentLinks();
  const [createDocumentLinks] = useCreateDocumentLinks();
  const { handleUpdateDocumentLink } = useHandleUpdateDocumentsLink();

  useEffect(() => {
    if (data) {
      setItems(data.documentLinks || []);
      setLoaded(true);
    }
    if (error) {
      showToast(t('DOCUMENTS_NOT_FOUND'), t('DOCUMENTS_NOT_FOUND'), 'error');
    }
  }, [data, error]);

  const fetchData = async () => {
    try {
      await refetch();
    } catch (err) {
      showToast(t('ERROR_FETCHING_DATA'), t('ERROR_FETCHING_DATA'), 'error');
    }
  };

  const getNewSop = (userId) => {
    return {
      link: 'https://',
      name: '',
      foreignKeys: [userId],
      type: 'SOP',
    };
  };

  const handleAddSop = () => {
    setSop(getNewSop(userId));
    setShowFormModal(true);
  };

  const handleEditSop = (item) => {
    setSop(item);
    setShowFormModal(true);
  };

  const handleDeleteSop = (id) => {
    setSop(id);
    setShowConfirmModal(true);
  };

  const handleHideConfirmModal = () => {
    setShowConfirmModal(false);
    setSop(null);
  };

  const handleHideSopForm = () => {
    setShowFormModal(false);
    setSop(null);
  };

  const handleConfirmDeleteSop = async () => {
    setShowConfirmModal(false);
    try {
      const result = await deleteDocuments(sop);
      if (result?.data?.ids?.length) {
        setItems((currentItems) =>
          currentItems.filter((item) => item.id !== result.data.ids[0]),
        );
      }

      setSop(null);
      showToast(
        t('SOP_SUCCESSFULLY_DELETED'),
        t('SOP_SUCCESSFULLY_DELETED'),
        'success',
      );
    } catch (e) {
      showToast(t('PROBLEM_DELETING_SOP'), t('PROBLEM_DELETING_SOP'), 'error');
    } finally {
      fetchData();
    }
  };

  const handleSaveForm = async (sop) => {
    try {
      let result;
      result = await createDocumentLinks(sop);
      const newItem = result?.data || [];
      if (newItem.length) {
        setItems((currentItems) => [...currentItems, ...newItem]);
      }

      setSop(null);
      setShowFormModal(false);
      showToast(
        t('SOP_SAVED_SUCCESSFULLY'),
        t('SOP_SAVED_SUCCESSFULLY'),
        'success',
      );
    } catch (e) {
      showToast(t('PROBLEM_SAVING_SOP'), t('PROBLEM_SAVING_SOP'), 'error');
    } finally {
      fetchData();
    }
  };

  const handleUpdateForm = async (sop) => {
    let result;
    const { name, link, id } = sop[0];
    result = await handleUpdateDocumentLink({ name, link }, id);
    const updatedData = result?.data;
    if (updatedData) {
      setItems((currentItems) =>
        currentItems.map((item) => {
          if (item.id === updatedData.id) {
            return updatedData;
          }

          return item;
        }),
      );
    }

    setSop(null);
    setShowFormModal(false);
  };

  const renderSopForm = () => {
    if (!showFormModal) {
      return null;
    }

    return (
      <SopForm
        sop={sop}
        onCancel={handleHideSopForm}
        onSave={!sop?.id ? handleSaveForm : handleUpdateForm}
      />
    );
  };

  const renderSopItems = () => {
    if (!loaded) {
      return (
        <div className="p-6" data-testid={`${pageTestId}-loading`}>
          <LoadingCircle text={t('LOADING_SOP')} />
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div
          className="px-6 pb-6"
          data-testid={`${pageTestId}-no-docs-container`}
        >
          <p
            className="mt-0 mb-4 pt-2"
            data-testid={`${pageTestId}-no-docs-text`}
          >
            {t('NO_SOP')}
          </p>
          {renderActionComponentBasedOnRole(
            <AddButton
              title={t('CREATE_NEW_SOP')}
              onClick={handleAddSop}
              dataTestId={`${pageTestId}-create-sop-button`}
            />,
          )}
        </div>
      );
    }

    const tableColumns = [
      {
        field: 'name',
        label: t('NAME'),
        render: (_, item) => (
          <div data-testid={`${pageTestId}-item-name`}>{item.name}</div>
        ),
      },
      {
        field: 'link',
        label: t('URL'),
        render: (_, item) => (
          <div data-testid={`${pageTestId}-item-link`}>
            <a target="_blank" href={item.link} rel="noreferrer">
              {truncate(item.link, 20)}
            </a>
          </div>
        ),
      },
      {
        field: 'action',
        label: t('ACTION', { count: 2 }),
        render: (_, item) =>
          renderActionComponentBasedOnRole(
            <div
              className="flex items-center"
              data-testid={`${pageTestId}-action-button`}
            >
              <div className="flex justify-end">
                <Dropdown useIcon>
                  <Dropdown.Button
                    dataTestId={`${pageTestId}-edit-button`}
                    onClick={() => handleEditSop(item)}
                  >
                    {t('EDIT')}
                  </Dropdown.Button>
                  <Dropdown.Button
                    dataTestId={`${pageTestId}-delete-button`}
                    onClick={() => handleDeleteSop(item.id)}
                  >
                    {titleize(t('REMOVE'))}
                  </Dropdown.Button>
                </Dropdown>
              </div>
            </div>,
          ),
      },
    ];

    return (
      <div className="overflow-x-auto" data-testid={`${pageTestId}-table`}>
        <Table
          headers={tableColumns}
          data={items}
          columns={tableColumns}
          tableClassName="min-w-full default-table"
          bodyClassName="bg-white"
          colWidths={[40, 40, 20]}
        />
      </div>
    );
  };

  const renderActionComponentBasedOnRole = (children) => {
    return (
      <AuthorizationWrapper requiredRoles={['shipper-onboarding']}>
        {children}
      </AuthorizationWrapper>
    );
  };

  return (
    <div data-testid={`${pageTestId}-items`}>
      <CargomaticCard
        removeHorizontalPadding
        removeVerticalPadding
        header={() => (
          <div
            className="flex items-center justify-between"
            data-testid={`${pageTestId}-add`}
          >
            <h3 className="my-0 text-lg font-medium leading-6">{t('SOP')}</h3>
            {renderActionComponentBasedOnRole(
              <AddButton
                title={t('ADD_NEW_SOP')}
                onClick={handleAddSop}
                dataTestId={`${pageTestId}-add-button`}
              />,
            )}
          </div>
        )}
      >
        {renderSopItems()}
      </CargomaticCard>
      {showFormModal && (
        <CargomaticModal
          show={showFormModal}
          onHide={handleHideSopForm}
          title={t('ADD_SOP')}
        >
          {renderSopForm()}
        </CargomaticModal>
      )}
      {showConfirmModal && (
        <ConfirmModal
          show={showConfirmModal}
          message={t('SOP_DELETION_CONFIRM')}
          title={t('CONFIRM_DELETION')}
          onConfirm={handleConfirmDeleteSop}
          onHide={handleHideConfirmModal}
        />
      )}
    </div>
  );
}
